import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  fr: {
    // App title
    appName: "PunchTM",
    appTagline: "Pointage Industriel",
    
    // Kiosk screens
    enterCode: "Entrez votre code",
    continue: "CONTINUER",
    chooseAction: "Choisissez votre action",
    clockIn: "ENTRÉE",
    breakStart: "PAUSE",
    breakReturn: "RETOUR PAUSE",
    clockOut: "SORTIE",
    takePhoto: "PRENDRE PHOTO",
    confirm: "CONFIRMER",
    retake: "REPRENDRE",
    punchRecorded: "Punch enregistré",
    returningIn: "Retour dans",
    seconds: "secondes",
    cameraAccess: "Accès caméra requis",
    enableCamera: "Veuillez activer la caméra pour continuer",
    
    // Status
    statusIn: "Présent",
    statusBreak: "En pause",
    statusOut: "Sorti",
    statusAbsent: "Absent",
    
    // Dashboard
    dashboard: "Tableau de bord",
    realTimePresence: "Présence temps réel",
    employee: "Employé",
    employees: "Employés",
    status: "Statut",
    lastPunch: "Dernier punch",
    time: "Heure",
    present: "Présents",
    onBreak: "En pause",
    out: "Sortis",
    total: "Total",
    
    // Reports
    reports: "Rapports",
    dailyReport: "Rapport journalier",
    date: "Date",
    site: "Site",
    zone: "Zone",
    filter: "Filtrer",
    entry: "Entrée",
    break: "Pause",
    return: "Retour",
    exit: "Sortie",
    totalHours: "Total heures",
    exportExcel: "Exporter Excel",
    exportPdf: "Exporter PDF",
    
    // Employee management
    employeeManagement: "Gestion employés",
    addEmployee: "Ajouter employé",
    editEmployee: "Modifier employé",
    employeeCode: "Code employé",
    firstName: "Prénom",
    lastName: "Nom",
    jobTitle: "Poste",
    active: "Actif",
    inactive: "Inactif",
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    confirmDelete: "Confirmer la suppression",
    
    // Sites & Zones
    sitesZones: "Sites & Zones",
    addSite: "Ajouter site",
    addZone: "Ajouter zone",
    description: "Description",
    
    // Settings
    settings: "Paramètres",
    companySettings: "Paramètres entreprise",
    standardBreakDuration: "Durée pause standard (min)",
    lateTolerance: "Tolérance retard (min)",
    activeSites: "Sites actifs",
    
    // Auth
    login: "Connexion",
    email: "Email",
    password: "Mot de passe",
    signIn: "Se connecter",
    signOut: "Déconnexion",
    invalidCredentials: "Identifiants invalides",
    
    // Navigation
    home: "Accueil",
    kiosk: "Kiosque",
    admin: "Administration",
    
    // Messages
    success: "Succès",
    error: "Erreur",
    loading: "Chargement...",
    noData: "Aucune donnée",
    employeeNotFound: "Employé non trouvé",
    invalidCode: "Code invalide (5 chiffres requis)",
    waitBeforePunch: "Veuillez patienter avant le prochain punch",
    invalidTransition: "Transition non autorisée",
    photoRequired: "Photo obligatoire",
    
    // Language
    language: "Langue",
    french: "Français",
    english: "English",
  },
  en: {
    // App title
    appName: "PunchTM",
    appTagline: "Industrial Time Tracking",
    
    // Kiosk screens
    enterCode: "Enter your code",
    continue: "CONTINUE",
    chooseAction: "Choose your action",
    clockIn: "CLOCK IN",
    breakStart: "BREAK",
    breakReturn: "RETURN",
    clockOut: "CLOCK OUT",
    takePhoto: "TAKE PHOTO",
    confirm: "CONFIRM",
    retake: "RETAKE",
    punchRecorded: "Punch recorded",
    returningIn: "Returning in",
    seconds: "seconds",
    cameraAccess: "Camera access required",
    enableCamera: "Please enable camera to continue",
    
    // Status
    statusIn: "Present",
    statusBreak: "On break",
    statusOut: "Out",
    statusAbsent: "Absent",
    
    // Dashboard
    dashboard: "Dashboard",
    realTimePresence: "Real-time Presence",
    employee: "Employee",
    employees: "Employees",
    status: "Status",
    lastPunch: "Last punch",
    time: "Time",
    present: "Present",
    onBreak: "On break",
    out: "Out",
    total: "Total",
    
    // Reports
    reports: "Reports",
    dailyReport: "Daily Report",
    date: "Date",
    site: "Site",
    zone: "Zone",
    filter: "Filter",
    entry: "Entry",
    break: "Break",
    return: "Return",
    exit: "Exit",
    totalHours: "Total hours",
    exportExcel: "Export Excel",
    exportPdf: "Export PDF",
    
    // Employee management
    employeeManagement: "Employee Management",
    addEmployee: "Add Employee",
    editEmployee: "Edit Employee",
    employeeCode: "Employee Code",
    firstName: "First Name",
    lastName: "Last Name",
    jobTitle: "Job Title",
    active: "Active",
    inactive: "Inactive",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    confirmDelete: "Confirm deletion",
    
    // Sites & Zones
    sitesZones: "Sites & Zones",
    addSite: "Add Site",
    addZone: "Add Zone",
    description: "Description",
    
    // Settings
    settings: "Settings",
    companySettings: "Company Settings",
    standardBreakDuration: "Standard break duration (min)",
    lateTolerance: "Late tolerance (min)",
    activeSites: "Active sites",
    
    // Auth
    login: "Login",
    email: "Email",
    password: "Password",
    signIn: "Sign In",
    signOut: "Sign Out",
    invalidCredentials: "Invalid credentials",
    
    // Navigation
    home: "Home",
    kiosk: "Kiosk",
    admin: "Admin",
    
    // Messages
    success: "Success",
    error: "Error",
    loading: "Loading...",
    noData: "No data",
    employeeNotFound: "Employee not found",
    invalidCode: "Invalid code (5 digits required)",
    waitBeforePunch: "Please wait before next punch",
    invalidTransition: "Invalid transition",
    photoRequired: "Photo required",
    
    // Language
    language: "Language",
    french: "Français",
    english: "English",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('punchtm-language');
    return saved || 'fr';
  });

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => {
      const newLang = prev === 'fr' ? 'en' : 'fr';
      localStorage.setItem('punchtm-language', newLang);
      return newLang;
    });
  }, []);

  const t = useCallback((key) => {
    return translations[language][key] || key;
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translations: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
